// features/buyer/chat/BuyerChats.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserChats } from '../../services/chatService';
import { 
  MessageCircle, 
  Clock, 
  Package,
  ArrowRight,
  ShoppingBag,
  Filter
} from 'lucide-react';

const BuyerChats = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'recent'

  useEffect(() => {
    const loadChats = async () => {
      if (!user) return;
      
      try {
        const userChats = await getUserChats(user.uid, 'buyer');
        setChats(userChats);
      } catch (error) {
        console.error('Error loading chats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadChats();
  }, [user]);

  const filteredChats = chats.filter(chat => {
    if (filter === 'unread') {
      return chat.unreadCount?.buyer > 0;
    }
    if (filter === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return new Date(chat.lastMessageTime) > sevenDaysAgo;
    }
    return true;
  });

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Conversations</h1>
            <p className="text-gray-600 mt-1">Chat with sellers about products</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <MessageCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{chats.length}</p>
                <p className="text-xs text-gray-500">Total Chats</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'all' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          All Chats
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'unread' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          Unread
        </button>
        <button
          onClick={() => setFilter('recent')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'recent' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          Recent (7 days)
        </button>
      </div>

      {filteredChats.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">No conversations found</p>
          <p className="text-sm text-gray-500 mt-2">
            {filter !== 'all' 
              ? 'No chats match your filter criteria' 
              : 'Start a conversation by clicking "Chat with Supplier" on any product'}
          </p>
          <Link
            to="/buyer/products"
            className="inline-flex items-center mt-6 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors"
          >
            <ShoppingBag className="h-4 w-4 mr-2" />
            Browse Products
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredChats.map((chat) => (
            <Link
              key={chat.id}
              to={`/buyer/chats/${chat.id}`}
              className="bg-white rounded-xl border hover:border-orange-300 hover:shadow-lg transition-all p-4"
            >
              <div className="flex items-start space-x-3">
                <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <Package className="h-7 w-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 truncate">{chat.productName}</h3>
                    <span className="text-xs text-gray-500">
                      {formatTime(chat.lastMessageTime)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 truncate">
                    Seller: {chat.sellerName}
                  </p>
                  <p className="text-sm text-gray-700 mt-2 truncate">
                    {chat.lastMessage}
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center space-x-2">
                      {chat.unreadCount?.buyer > 0 && (
                        <span className="px-2 py-1 text-xs font-bold bg-orange-600 text-white rounded-full">
                          {chat.unreadCount.buyer} unread
                        </span>
                      )}
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                        {chat.sellerVerified ? 'Verified Seller' : 'Seller'}
                      </span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default BuyerChats;